// `PlanGrid` — 4-tier plan grid for Settings → Abonnement (US-192).
//
// Renders Discover / Pro / Scale / Agency in a 2×2 grid. Active plan card
// gets the accent border + "Actif" pill. Pro is flagged with the
// "Le plus populaire" tag (per `billing.md` §Canonical plans).
//
// Selecting a non-active card calls `onSelect(plan)` — parent decides
// whether to open the upgrade or downgrade confirm modal.
//
// `cycle` drives the displayed Stripe price (monthly / yearly). No amount or
// yearly discount is derived from product copy in the browser.

import { useLocale, useT } from '@barel/i18n';
import { Button, Pill } from '@barel/ui';

import type { BillingCycle, BillingPlan, BillingPrice } from '@barel/api-client/react';

import {
  BILLING_PLAN_ORDER,
  findBillingPrice,
  formatBillingAmount,
  formatBillingPrice,
  isPlanUpgrade,
} from './prices';

const PLAN_LIMITS_DISPLAY: Record<
  BillingPlan,
  { accounts: number; campaigns: number | 'unlimited'; ai: number | 'unlimited' }
> = {
  discover: { accounts: 1, campaigns: 1, ai: 500 },
  pro: { accounts: 1, campaigns: 5, ai: 1500 },
  scale: { accounts: 2, campaigns: 10, ai: 4000 },
  agency: { accounts: 3, campaigns: 'unlimited', ai: 'unlimited' },
};

export interface PlanGridProps {
  activePlan: BillingPlan;
  prices: readonly BillingPrice[];
  /** Billing cycle currently selected — drives price label. Default `monthly`. */
  cycle?: BillingCycle;
  onSelect: (plan: BillingPlan) => void;
  /** Disable cards (e.g., while a mutation is in flight). */
  disabled?: boolean;
  /** Override popular plan for tests. */
  popularPlan?: BillingPlan;
}

export function PlanGrid({
  activePlan,
  prices,
  cycle = 'monthly',
  onSelect,
  disabled,
  popularPlan = 'pro',
}: PlanGridProps) {
  const t = useT('billing');

  return (
    <div data-component="PlanGrid" data-cycle={cycle} className="flex flex-col gap-3">
      <div className="text-2xs font-semibold uppercase tracking-wider text-tertiary">
        {t('plans.eyebrow')}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {BILLING_PLAN_ORDER.map((plan) => (
          <PlanCard
            key={plan}
            plan={plan}
            activePlan={activePlan}
            cycle={cycle}
            active={plan === activePlan}
            popular={plan === popularPlan && plan !== activePlan}
            disabled={disabled || plan === activePlan}
            price={findBillingPrice(prices, plan, cycle)}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

interface PlanCardProps {
  plan: BillingPlan;
  activePlan: BillingPlan;
  cycle: BillingCycle;
  active: boolean;
  popular: boolean;
  disabled: boolean;
  price: BillingPrice | undefined;
  onSelect: (plan: BillingPlan) => void;
}

function PlanCard({
  plan,
  activePlan,
  cycle,
  active,
  popular,
  disabled,
  price,
  onSelect,
}: PlanCardProps) {
  const t = useT('billing');
  const locale = useLocale();
  const isUpgrade = isPlanUpgrade(activePlan, plan);
  const limits = PLAN_LIMITS_DISPLAY[plan];

  const accountsLabel =
    limits.accounts === 1
      ? t('plans.feature.accountsOne')
      : t('plans.feature.accountsMany', { count: limits.accounts });
  const campaignsLabel =
    limits.campaigns === 'unlimited'
      ? t('plans.feature.campaignsUnlimited')
      : limits.campaigns === 1
        ? t('plans.feature.campaignsOne')
        : t('plans.feature.campaignsMany', { count: limits.campaigns });
  const aiLabel =
    limits.ai === 'unlimited'
      ? t('plans.feature.aiUnlimited')
      : t('plans.feature.aiCalls', { count: limits.ai });

  // Keep yearly plans comparable with monthly ones, while the exact annual
  // Stripe amount remains visible on the line below.
  const headlinePrice = price
    ? cycle === 'yearly'
      ? formatBillingAmount(price.unitAmount / 12, price.currency, locale)
      : formatBillingPrice(price, locale)
    : '—';

  return (
    <div
      data-plan={plan}
      data-active={active || undefined}
      data-popular={popular || undefined}
      className={[
        'relative flex flex-col rounded-xl border p-4',
        active
          ? 'border-[1.5px] border-accent bg-bg-elevated'
          : popular
            ? 'border-accent-border bg-bg-surface'
            : 'border-default bg-bg-surface',
      ].join(' ')}
    >
      {popular ? (
        <div className="absolute -top-2 left-3 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          {t('plans.popularBadge')}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <span className="text-lg" aria-hidden>
          {t(`plans.${plan}.emoji`)}
        </span>
        <div className="flex-1 text-sm font-semibold text-primary">{t(`plans.${plan}.name`)}</div>
        {active ? (
          <Pill tone="accent" variant="soft" size="sm">
            {t('plans.activeBadge')}
          </Pill>
        ) : null}
      </div>

      <div className="mt-3 text-2xl font-bold tabular-nums leading-none text-primary">
        {t('summary.perMonth', { price: headlinePrice })}
      </div>
      {cycle === 'yearly' && price ? (
        <div
          className="mt-1 text-xs text-tertiary tabular-nums"
          data-testid={`plan-yearly-${plan}`}
        >
          {t('summary.perYear', { price: formatBillingPrice(price, locale) })}
        </div>
      ) : (
        <div
          className="mt-1 text-xs text-tertiary tabular-nums"
          data-testid={`plan-monthly-${plan}`}
        >
          {price
            ? t('summary.perMonth', { price: formatBillingPrice(price, locale) })
            : t('plans.priceUnavailable')}
        </div>
      )}

      <ul className="mt-3 flex flex-1 list-none flex-col gap-1.5 p-0 text-xs text-secondary">
        <FeatureRow label={accountsLabel} />
        <FeatureRow label={campaignsLabel} />
        <FeatureRow label={aiLabel} />
        {plan === 'agency' ? <FeatureRow label={t('plans.feature.smartLimits')} /> : null}
      </ul>

      <div className="mt-4">
        <Button
          variant={active ? 'ghost' : 'primary'}
          size="sm"
          fullWidth
          disabled={disabled || !price}
          onClick={() => onSelect(plan)}
          data-testid={`plan-cta-${plan}`}
        >
          {active
            ? t('plans.currentCta')
            : isUpgrade
              ? t('plans.upgradeCta', { name: t(`plans.${plan}.name`) })
              : t('plans.downgradeCta', { name: t(`plans.${plan}.name`) })}
        </Button>
      </div>
    </div>
  );
}

function FeatureRow({ label }: { label: string }) {
  return (
    <li className="flex items-start gap-2 leading-snug">
      <span className="font-bold text-accent" aria-hidden>
        ✓
      </span>
      <span>{label}</span>
    </li>
  );
}
